/**
 * ClientReportsHub — Focused per-client reporting.
 *
 * One mandatory client picker. Date range with presets. Then 4 KPI tiles
 * (Total · On Time · Delayed · Overdue), 3 content pillar cards (Posts ·
 * Reels · Stories), and a Tasks-by-Department horizontal bar chart.
 *
 * Designed for clarity, accuracy, zero ambiguity.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Briefcase, Search, Filter as FilterIcon, X,
  CheckCircle2, TimerOff, AlertTriangle, ListTodo,
  Film, FileText, BookOpen, Building2,
} from 'lucide-react';
import { formatDateToLocal } from '../../lib/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

// ─── Date presets ──────────────────────────────────────────────────────────
const PRESETS = [
  { key: '30d', label: 'Last 30d', compute: () => offset(30) },
  { key: '90d', label: 'Last 90d', compute: () => offset(90) },
  { key: 'mtd', label: 'MTD',      compute: () => monthToDate() },
  { key: 'qtd', label: 'QTD',      compute: () => quarterToDate() },
  { key: 'ytd', label: 'YTD',      compute: () => yearToDate() },
];
function offset(days) {
  const to = new Date();
  const from = new Date(); from.setDate(from.getDate() - days);
  return [formatDateToLocal(from), formatDateToLocal(to)];
}
function monthToDate() {
  const t = new Date();
  return [formatDateToLocal(new Date(t.getFullYear(), t.getMonth(), 1)), formatDateToLocal(t)];
}
function quarterToDate() {
  const t = new Date();
  const qStart = new Date(t.getFullYear(), Math.floor(t.getMonth() / 3) * 3, 1);
  return [formatDateToLocal(qStart), formatDateToLocal(t)];
}
function yearToDate() {
  const t = new Date();
  return [formatDateToLocal(new Date(t.getFullYear(), 0, 1)), formatDateToLocal(t)];
}

// ─── Compact filter bar ────────────────────────────────────────────────────
const SegBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-2.5 py-1 text-[11px] rounded-md border transition-all ${
      active
        ? 'bg-pink-50 text-pink-700 border-pink-300 font-medium shadow-sm'
        : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
    }`}
  >{children}</button>
);

const FieldLabel = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-1 text-[10px] font-medium text-neutral-500 uppercase tracking-wider mb-1">
    {Icon && <Icon className="w-3 h-3" />}{children}
  </div>
);

const ClientFilterBar = ({ filters, setFilters, clients, search, setSearch }) => {
  const update = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const activePresetKey = useMemo(() => {
    for (const p of PRESETS) {
      const [f, t] = p.compute();
      if (f === filters.fromDate && t === filters.toDate) return p.key;
    }
    return null;
  }, [filters.fromDate, filters.toDate]);

  const filteredClients = useMemo(() => {
    if (!search) return clients;
    const s = search.toLowerCase();
    return clients.filter((c) => (c.name || '').toLowerCase().includes(s));
  }, [clients, search]);

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden" data-testid="client-filter-bar">
      <div className="flex items-center justify-between bg-gradient-to-r from-pink-50/60 via-white to-white border-b border-neutral-100 px-4 py-2">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1a1a2e]">
          <FilterIcon className="w-4 h-4 text-pink-600" /> Filters
        </div>
        {filters.clientId && (
          <button
            onClick={() => update({ clientId: '' })}
            data-testid="client-filter-clear"
            className="inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-pink-700 transition-colors"
          ><X className="w-3 h-3" /> Reset</button>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[280px]">
            <FieldLabel icon={Briefcase}>Client</FieldLabel>
            <div className="relative">
              {/* Client searchable select */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search client…"
                  data-testid="client-search"
                  className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 w-44"
                />
                <select
                  value={filters.clientId}
                  onChange={(e) => update({ clientId: e.target.value })}
                  data-testid="client-select"
                  className="flex-1 px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
                >
                  <option value="">— Pick a client —</option>
                  {filteredClients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.category ? ` · ${c.category}` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="text-[10px] text-neutral-400 mt-1">
                {filteredClients.length} of {clients.length} clients
              </div>
            </div>
          </div>

          <div>
            <FieldLabel icon={Calendar}>From</FieldLabel>
            <input
              type="date" value={filters.fromDate}
              onChange={(e) => update({ fromDate: e.target.value })}
              data-testid="client-from-date"
              className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
            />
          </div>
          <div>
            <FieldLabel icon={Calendar}>To</FieldLabel>
            <input
              type="date" value={filters.toDate}
              onChange={(e) => update({ toDate: e.target.value })}
              data-testid="client-to-date"
              className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
            />
          </div>
          <div className="flex items-center gap-1.5 pb-0.5">
            {PRESETS.map((p) => (
              <SegBtn
                key={p.key}
                active={activePresetKey === p.key}
                onClick={() => {
                  const [f, t] = p.compute();
                  update({ fromDate: f, toDate: t });
                }}
                testid={`client-preset-${p.key}`}
              >{p.label}</SegBtn>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── KPI Tile ──────────────────────────────────────────────────────────────
const Tile = ({ icon: Icon, label, value, hint, tone = 'neutral', testid }) => {
  const toneCls = {
    pink: 'text-pink-700', emerald: 'text-emerald-700', amber: 'text-amber-700',
    red: 'text-red-700', blue: 'text-blue-700', neutral: 'text-neutral-800',
  }[tone] || 'text-neutral-800';
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${toneCls}`} />}
      </div>
      <div className={`text-[28px] font-semibold leading-none ${toneCls} tabular-nums`}>{value}</div>
      <div className="text-[11px] text-neutral-400 mt-2">{hint}</div>
    </div>
  );
};

// ─── Content Pillar Card (Reel / Post / Story) ─────────────────────────────
const PillarCard = ({ icon: Icon, label, count, total, color, testid }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center`} style={{ background: `${color}1a` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-[#1a1a2e]">{label}</div>
            <div className="text-[10px] text-neutral-500">in selected window</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[26px] font-semibold tabular-nums leading-none" style={{ color }}>{count}</div>
          <div className="text-[10px] text-neutral-400 mt-1">{pct}% of total</div>
        </div>
      </div>
      {/* Mini progress bar */}
      <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

// ─── Tasks-by-Department Horizontal Bar Chart ──────────────────────────────
const DEPT_COLORS = {
  Design: '#EC4899', Production: '#F59E0B', 'Social Media': '#3B82F6',
  Video: '#8B5CF6', Content: '#10B981', Tech: '#EF4444', Boss: '#737373',
  Uncategorized: '#A3A3A3',
};

const DepartmentBars = ({ byDepartment }) => {
  const entries = Object.entries(byDepartment || {}).filter(([k]) => k && k !== '');
  if (entries.length === 0) {
    return (
      <div className="text-center py-10 text-sm text-neutral-500">
        No tasks created in this window for this client.
      </div>
    );
  }
  // Sort by count desc
  entries.sort(([, a], [, b]) => b - a);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  const max = Math.max(1, ...entries.map(([, n]) => n));
  return (
    <div className="space-y-2.5">
      {entries.map(([dept, n]) => {
        const color = DEPT_COLORS[dept] || '#A3A3A3';
        const pct = total > 0 ? Math.round((n / total) * 100) : 0;
        return (
          <div key={dept} className="grid grid-cols-12 gap-3 items-center text-[12px]">
            <div className="col-span-3 inline-flex items-center gap-2 text-neutral-700 truncate">
              <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: color }} />
              <span className="truncate">{dept}</span>
            </div>
            <div className="col-span-7">
              <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${(n / max) * 100}%`, background: color }} />
              </div>
            </div>
            <div className="col-span-2 text-right text-neutral-700 tabular-nums">
              <span className="font-medium">{n}</span>
              <span className="text-neutral-400 ml-1.5">({pct}%)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Main hub ──────────────────────────────────────────────────────────────
const ClientReportsHub = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [filters, setFilters] = useState({
    clientId: '',
    fromDate: formatDateToLocal(firstOfMonth),
    toDate: formatDateToLocal(today),
  });
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load client list once
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/admin/reports/clients/list`, { headers: auth() })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.clients || [];
        setClients(list);
        // Auto-pick first client to avoid empty initial state
        if (list.length > 0 && !filters.clientId) {
          setFilters((p) => ({ ...p, clientId: list[0].id }));
        }
      })
      .catch((e) => !cancelled && toast.error(e.response?.data?.detail || 'Failed to load clients'));
    return () => { cancelled = true; };
  }, []);

  // Load overview whenever filters change
  useEffect(() => {
    if (!filters.clientId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true); setError(null);
    axios.get(`${API}/api/admin/reports/clients/overview`, {
      headers: auth(),
      params: {
        client_id: filters.clientId,
        from_date: filters.fromDate,
        to_date: filters.toDate,
      },
    })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.detail || 'Failed to load metrics');
        setData(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [filters.clientId, filters.fromDate, filters.toDate]);

  const t = data?.tiles || {};
  const bySub = data?.by_subtype || {};
  const total = t.total_tasks ?? 0;

  return (
    <div className="space-y-6" data-testid="client-reports-hub">
      <ClientFilterBar
        filters={filters} setFilters={setFilters}
        clients={clients} search={search} setSearch={setSearch}
      />

      {!filters.clientId ? (
        <div className="bg-white border border-dashed border-neutral-300 rounded-xl p-12 text-center">
          <Briefcase className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-neutral-700 mb-1">Pick a client to view their report</div>
          <p className="text-xs text-neutral-500 max-w-md mx-auto">
            Use the search + dropdown above. Reports are accurate, scoped to the selected window, and update live.
          </p>
        </div>
      ) : loading && !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white border border-neutral-200 rounded-xl p-4 animate-pulse h-[110px]" />
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
      ) : data ? (
        <>
          {/* Client persona strip */}
          <div className="bg-gradient-to-r from-pink-50/80 via-white to-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="client-persona">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-100 text-pink-700 flex items-center justify-center font-semibold text-sm">
                {(data.client.name || '?').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="text-[15px] font-semibold text-[#1a1a2e]">{data.client.name}</div>
                <div className="text-[11px] text-neutral-500">
                  {data.client.category && data.client.category !== '—' ? data.client.category + ' · ' : ''}
                  Window: {data.from} → {data.to}
                </div>
              </div>
            </div>
          </div>

          {/* Row 1 — Core KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile
              icon={ListTodo} label="Total tasks" value={total}
              hint="created in window" tone="pink" testid="client-tile-total"
            />
            <Tile
              icon={CheckCircle2} label="On time"
              value={t.on_time ?? 0}
              hint={`${t.on_time_rate ?? 0}% of completed (with due date)`}
              tone={t.on_time_rate >= 80 ? 'emerald' : t.on_time_rate >= 50 ? 'amber' : 'red'}
              testid="client-tile-on-time"
            />
            <Tile
              icon={TimerOff} label="Delayed"
              value={t.delayed ?? 0}
              hint={`${t.delayed_rate ?? 0}% of completed`}
              tone={t.delayed === 0 ? 'emerald' : t.delayed_rate <= 20 ? 'amber' : 'red'}
              testid="client-tile-delayed"
            />
            <Tile
              icon={AlertTriangle} label="Overdue (now)"
              value={t.overdue ?? 0}
              hint="active tasks past due"
              tone={t.overdue === 0 ? 'emerald' : 'red'}
              testid="client-tile-overdue"
            />
          </div>

          {/* Row 2 — Content pillars (Posts / Reels / Stories) */}
          <div>
            <div className="text-[12px] font-medium text-neutral-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              Content output
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <PillarCard
                icon={FileText} label="Posts" count={bySub.Post ?? 0} total={total}
                color="#3B82F6" testid="client-pillar-post"
              />
              <PillarCard
                icon={Film} label="Reels" count={bySub.Reel ?? 0} total={total}
                color="#EC4899" testid="client-pillar-reel"
              />
              <PillarCard
                icon={BookOpen} label="Stories" count={bySub.Story ?? 0} total={total}
                color="#F59E0B" testid="client-pillar-story"
              />
            </div>
          </div>

          {/* Row 3 — Tasks by department */}
          <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="client-by-dept">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-pink-600" />
              <div>
                <div className="text-[13px] font-semibold text-[#1a1a2e]">Tasks by department</div>
                <div className="text-[11px] text-neutral-500">Sorted by volume · % of total in window</div>
              </div>
            </div>
            <DepartmentBars byDepartment={data.by_department} />
          </div>

          {/* Footer note about edge cases — keeps users informed */}
          {(t.completed_no_due_date ?? 0) > 0 && (
            <div className="text-[11px] text-neutral-500 bg-neutral-50 border border-neutral-100 rounded-md px-3 py-2">
              Note: {t.completed_no_due_date} completed task(s) lack a due_date and aren&apos;t counted in on-time/delayed math.
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default ClientReportsHub;
